/**
 * AccountSettingsSection
 *
 * Identity and preferences page reachable via the sidebar profile popup's
 * "Account & settings" row. Uses the standard list-page shell (sticky
 * `.page-header__title` + subtitle row over an `.explore-view--list` scroll
 * container) with the shared `.settings-column` card column — the same
 * arrangement as /partner, /help and /memory.
 *
 * Sections:
 *   - Profile (identity + inline name edit; tier badge and an Upgrade/Manage
 *     action below the meta row, then divided partner and sign-out rows.
 *     Subscription and partner actions hand off to existing pages — no flow
 *     forking; sign-out lives here rather than in a card of its own because
 *     it is an identity action, not a settings group)
 *
 * Every row action here is the plain bordered `.account-btn`, matching
 * /partner's Disconnect. The modifiers are reserved for the roles they carry
 * across both pages: --ghost cancels, --primary submits, --danger is the final
 * confirm inside a confirm panel. Sign-out has no confirm step, so it is not
 * --danger.
 *   - Sign-in & security (inline change-password form for email users)
 *   - Notifications (two email-preference switches; hidden for guests)
 *   - Privacy & data (placeholder — export/delete)
 *
 * Inline edit state is held on the instance (editingName, changingPassword)
 * and the whole card re-renders on transitions. This is cheap — no
 * animations or expensive children — and keeps handlers simple.
 *
 * Notification preferences are deliberately NOT mirrored onto the instance:
 * they render straight from window.app.currentUser, with only an in-flight
 * optimistic override held locally. The section is a long-lived singleton
 * whose activate() early-returns when already active, so any seeded copy
 * would go stale.
 *
 * Subscription plan (from the shared snapshot in core/subscriptionStatus.js)
 * and partner status are resolved async on activate, with
 * monotonic fetch tokens to discard stale responses. A `partner:unlinked`
 * eventBus listener keeps the partner card in sync when PartnerSection
 * unlinks elsewhere.
 */

import { API } from '../core/api.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import {
  fetchSubscriptionStatus as fetchSharedStatus,
  planFromStatus,
  isPaidPlan,
  planName
} from '../core/subscriptionStatus.js';
import { eventBus, escapeHtml } from '../core/utils.js';
import { createProfileAvatar } from '../utils/profileAvatar.js';
import { goToUpgrade } from '../utils/upgradeNavigation.js';
import { renderSwitchRow } from '../components/uiSwitch.js';

/**
 * Defaults used when the current user payload predates notification
 * preferences (or the user is a locally-constructed guest). Mirrors the
 * database defaults: product mail on, promotional mail off.
 */
const DEFAULT_NOTIFICATION_PREFS = { systemEmails: true, marketingEmails: false };

/**
 * Format a Date or ISO string as "March 2026". Returns empty string on
 * missing/invalid input so the memberSince line can be conditionally shown.
 */
function formatMemberSince(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export class AccountSettingsSection {
  /**
   * @param {Object} elements
   * @param {HTMLElement} elements.container - The #account-settings-page div
   * @param {EventBus} [eventBus]
   */
  constructor(elements, eventBus) {
    this.container = elements.container;
    this.eventBus = eventBus;
    this.isActive = false;

    // Inline UI state
    this.editingName = false;
    this.nameSaving = false;
    this.changingPassword = false;
    this.passwordSaving = false;
    this.passwordError = '';

    // Notification switches: `prefsSaving` disables both while a PATCH is in
    // flight; `prefsOverride` holds the optimistic value until it resolves.
    this.prefsSaving = false;
    this.prefsOverride = null;

    // Async-resolved status. `null` = never resolved (render placeholder),
    // 'basic'|'premium'|'lapsed'|'past_due' for plan (lapsed = UNPAID/CANCELED
    // history, past_due = payment failed but still in grace), partner
    // object|undefined for partner.
    /** @type {'basic'|'premium'|'lapsed'|'past_due'|null} */
    this.subscriptionPlan = null;
    this.partner = undefined;

    // Monotonic tokens guard against stale async responses overwriting the
    // UI after a newer fetch has started.
    this.subscriptionFetchToken = 0;
    this.partnerFetchToken = 0;

    this.discoveryApi = new DiscoveryAPI();

    this.handleClick = this.handleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePartnerUnlinked = this.handlePartnerUnlinked.bind(this);
  }

  async activate() {
    if (this.isActive) return;
    this.isActive = true;
    this.render();
    this.attachListeners();
    eventBus.on('partner:unlinked', this.handlePartnerUnlinked);
    this.fetchSubscriptionStatus();
    this.fetchPartner();
  }

  deactivate() {
    if (!this.isActive) return;
    this.isActive = false;
    this.detachListeners();
    eventBus.off?.('partner:unlinked', this.handlePartnerUnlinked);
  }

  /**
   * Current authenticated user. Read lazily from window.app so the section
   * stays resilient to initialization order and picks up locally-mutated
   * fields after successful saves.
   */
  get user() {
    return window.app?.currentUser || null;
  }

  /**
   * Re-render the page and keep focus on the active control, if any.
   * Called after every state transition or successful save.
   *
   * `focus` accepts 'name-input', 'current-password', or 'action:<data-action>'
   * — the last restores focus to a button the re-render destroyed, which the
   * notification switches need since they re-render twice per click.
   *
   * With no explicit target, focus is carried over from whatever control held
   * it: the background subscription and partner fetches re-render this whole
   * card whenever they land, which would otherwise drop the user out of the
   * control they were operating.
   */
  rerender({ focus } = {}) {
    const carriedAction = (!focus && this.container.contains(document.activeElement))
      ? document.activeElement.getAttribute?.('data-action')
      : null;

    this.render();
    if (!focus && carriedAction) {
      const carried = this.container.querySelector(`[data-action="${carriedAction}"]`);
      if (carried) carried.focus();
      return;
    }
    if (focus === 'name-input') {
      const input = this.container.querySelector('#account-name-input');
      if (input) {
        input.focus();
        input.select();
      }
    } else if (focus === 'current-password') {
      const input = this.container.querySelector('#account-current-password');
      if (input) input.focus();
    } else if (typeof focus === 'string' && focus.startsWith('action:')) {
      const button = this.container.querySelector(`[data-action="${focus.slice('action:'.length)}"]`);
      if (button) button.focus();
    }
  }

  render() {
    const user = this.user;
    const isGoogle = user?.authProvider === 'google';

    this.container.innerHTML = `
      <div class="explore-section">
        <div class="explore-container">
          <!-- Single-slice surface: the title is the only pinned row, so the
               base [data-sticky-slice] rule is the whole behaviour and no
               StickyStack controller is needed (see components/sticky-stack.css). -->
          <div class="explore-view--list" data-sticky-stack>
            <h2 class="page-header__title content-width" data-sticky-slice>Account</h2>

            <div class="page-header content-width">
              <div class="page-header__subtitle-row">
                <p class="page-header__subtitle">Manage your account and preferences</p>
              </div>
            </div>

            <div class="settings-column">
              ${this.renderProfileSection(user, isGoogle)}
              ${this.renderSecuritySection(user, isGoogle)}
              ${this.renderNotificationsSection()}
              ${this.renderPrivacySection()}
            </div>
          </div>
        </div>
      </div>
    `;

    this.populateAvatar();
  }

  renderProfileSection(user, isGoogle) {
    const displayName = user?.firstName || user?.name || user?.username || 'You';
    const memberSince = formatMemberSince(user?.createdAt);
    const providerLine = isGoogle ? 'Signed in with Google' : 'Signed in with email';

    const metaParts = [];
    if (memberSince) {
      metaParts.push(`<span class="account-profile__meta-item">Member since ${escapeHtml(memberSince)}</span>`);
    }
    metaParts.push(`<span class="account-profile__meta-item">${escapeHtml(providerLine)}</span>`);

    const nameBlock = this.editingName
      ? this.renderNameEditor(displayName)
      : `
        <div class="account-profile__name-row">
          <p class="account-profile__name">${escapeHtml(displayName)}</p>
          <button type="button" class="account-btn" data-action="edit-name">Edit</button>
        </div>
      `;

    return `
      <section class="account-section">
        <h2 class="account-section__label">Profile</h2>
        <div class="card account-section__card">
          <div class="account-profile">
            <span class="account-profile__avatar" id="account-profile-avatar"></span>
            <div class="account-profile__info">
              ${nameBlock}
              <p class="account-profile__meta">${metaParts.join('')}</p>
            </div>
          </div>
          ${this.renderSubscriptionInline()}
          ${this.renderPartnerRow()}
          ${this.renderSignOutRow()}
        </div>
      </section>
    `;
  }

  /**
   * Subscription line inside the Profile card: plan badge + an action that
   * hands off to /upgrade (Basic) or /subscription (any paid plan, lapsed
   * users — who get the in-place reactivation card there — and past-due
   * users, who get the payment-issue card). Mirrors ProfilePopup's wiring —
   * same lapsed/past-due detection, same routes, same precedence — so we
   * don't fork the routing. Lapsed keeps the Basic badge, as in the popup;
   * past-due swaps the Premium badge for "Payment issue", as in the popup.
   * Returns empty string until the async fetch resolves to avoid a flash.
   */
  renderSubscriptionInline() {
    const plan = this.subscriptionPlan;
    if (plan === null) return '';

    const isPastDue = plan === 'past_due';
    const isPaid = isPaidPlan(plan);
    const badgeClass = isPastDue
      ? 'account-tier-badge account-tier-badge--payment-issue'
      : (isPaid
        ? 'account-tier-badge account-tier-badge--premium'
        : 'account-tier-badge account-tier-badge--free');
    const badgeLabel = isPastDue ? 'Payment issue' : `${planName(plan)} plan`;
    const linkLabel = isPastDue
      ? 'Fix payment'
      : (isPaid ? 'Manage' : (plan === 'lapsed' ? 'Reactivate' : 'Upgrade'));

    return `
      <p class="account-profile__subscription">
        <span class="account-profile__subscription-badge">
          <span class="${badgeClass}">${badgeLabel}</span>
        </span>
        <button type="button" class="account-btn" data-action="manage-subscription">
          ${linkLabel}
        </button>
      </p>
    `;
  }

  /**
   * Partner row, rendered inside the Profile card below a horizontal
   * divider. Shows the linked partner's avatar + name (with a green status
   * dot) or an invite CTA. The button always navigates to /partner — that
   * page surfaces the invite flow when unlinked and management UI when
   * linked. Mirrors ProfilePopup's behaviour so we don't fork the partner
   * flow.
   *
   * The small avatar (added alongside the dot, not instead of it — the dot
   * is the "connected" affirmation, the avatar is who) is this page's only
   * identity-bearing row that had no face: every other partner surface
   * (/partner, the dashboard) shows one, so this row read as bare metadata
   * next to them.
   *
   * Three states:
   *   undefined → never resolved (loading placeholder)
   *   null      → fetched, not linked
   *   object    → fetched, linked
   */
  renderPartnerRow() {
    const partner = this.partner;
    const isResolved = partner !== undefined;
    const linked = !!(partner && (partner.firstName || partner.name || partner.username));
    const partnerName = linked
      ? (partner.firstName || partner.name || partner.username)
      : '';

    let valueHtml;
    if (!isResolved) {
      valueHtml = '<span class="account-profile__partner-text">Loading…</span>';
    } else if (linked) {
      valueHtml = `
        <span class="account-profile__partner-text account-partner-status">
          <span class="account-partner-status__avatar" data-profile-avatar="partner" aria-hidden="true"></span>
          <span class="account-partner-status__dot" aria-hidden="true"></span>
          Linked with ${escapeHtml(partnerName)}
        </span>
      `;
    } else {
      valueHtml = '<span class="account-profile__partner-text">Invite your partner to share insights.</span>';
    }

    let actionHtml = '';
    if (isResolved) {
      const linkLabel = linked ? 'Manage' : 'Link';
      actionHtml = `<button type="button" class="account-btn" data-action="manage-partner">${linkLabel}</button>`;
    }

    return `
      <p class="account-profile__partner">
        ${valueHtml}
        ${actionHtml}
      </p>
    `;
  }

  /**
   * Sign-out row, the last divided row in the Profile card. Signing out is an
   * identity action, so it belongs beside the identity it ends rather than in
   * a settings card of its own. Emits the same `profile:signout` event the
   * sidebar profile popup does — one flow, two entry points.
   */
  renderSignOutRow() {
    return `
      <p class="account-profile__signout">
        <span class="account-profile__signout-text">End your session on this device.</span>
        <button type="button" class="account-btn" data-action="sign-out">
          Sign out
        </button>
      </p>
    `;
  }

  renderNameEditor(currentValue) {
    const disabled = this.nameSaving ? 'disabled' : '';
    return `
      <form class="account-edit" data-form="name">
        <input
          type="text"
          id="account-name-input"
          class="account-edit__input"
          name="firstName"
          value="${escapeHtml(currentValue)}"
          maxlength="80"
          autocomplete="given-name"
          ${disabled}
          aria-label="First name"
        />
        <div class="account-edit__actions">
          <button type="button" class="account-btn account-btn--ghost" data-action="cancel-name" ${disabled}>Cancel</button>
          <button type="submit" class="account-btn account-btn--primary" ${disabled}>${this.nameSaving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    `;
  }

  renderSecuritySection(user, isGoogle) {
    const email = escapeHtml(user?.email || '');

    const passwordRow = isGoogle
      ? `
        <div class="account-row">
          <div class="account-row__body">
            <span class="account-row__label">Password</span>
            <span class="account-row__value">Managed by your Google account</span>
          </div>
        </div>
      `
      : this.changingPassword
        ? `
          <div class="account-row account-row--stacked">
            <div class="account-row__body">
              <span class="account-row__label">Change password</span>
              <span class="account-row__value">Must be at least 8 characters and different from your current password.</span>
            </div>
          </div>
          ${this.renderPasswordForm()}
        `
        : `
          <div class="account-row">
            <div class="account-row__body">
              <span class="account-row__label">Password</span>
              <span class="account-row__value">Change your password without leaving this page.</span>
            </div>
            <div class="account-row__action">
              <button type="button" class="account-btn" data-action="change-password">
                Change password
              </button>
            </div>
          </div>
        `;

    return `
      <section class="account-section">
        <h2 class="account-section__label">Sign-in &amp; security</h2>
        <div class="card account-section__card">
          ${passwordRow}
          <div class="account-row">
            <div class="account-row__body">
              <span class="account-row__label">Email</span>
              <span class="account-row__value">${email}</span>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  renderPasswordForm() {
    const disabled = this.passwordSaving ? 'disabled' : '';
    const errorHtml = this.passwordError
      ? `<p class="account-form__error" role="alert">${escapeHtml(this.passwordError)}</p>`
      : '';
    return `
      <form class="account-form" data-form="password" autocomplete="off">
        <div class="account-form__field">
          <label class="account-form__label" for="account-current-password">Current password</label>
          <input
            type="password"
            id="account-current-password"
            class="account-form__input"
            name="currentPassword"
            autocomplete="current-password"
            required
            ${disabled}
          />
        </div>
        <div class="account-form__field">
          <label class="account-form__label" for="account-new-password">New password</label>
          <input
            type="password"
            id="account-new-password"
            class="account-form__input"
            name="newPassword"
            minlength="8"
            autocomplete="new-password"
            required
            ${disabled}
          />
        </div>
        <div class="account-form__field">
          <label class="account-form__label" for="account-confirm-password">Confirm new password</label>
          <input
            type="password"
            id="account-confirm-password"
            class="account-form__input"
            name="confirmPassword"
            minlength="8"
            autocomplete="new-password"
            required
            ${disabled}
          />
        </div>
        ${errorHtml}
        <div class="account-form__actions">
          <button type="button" class="account-btn account-btn--ghost" data-action="cancel-change-password" ${disabled}>Cancel</button>
          <button type="submit" class="account-btn account-btn--primary" ${disabled}>${this.passwordSaving ? 'Saving…' : 'Save password'}</button>
        </div>
      </form>
    `;
  }

  /**
   * Current notification preferences: the saved values from the user payload,
   * with any in-flight optimistic change layered on top.
   */
  get notificationPrefs() {
    return {
      ...DEFAULT_NOTIFICATION_PREFS,
      ...(this.user?.notificationPreferences || {}),
      ...(this.prefsOverride || {})
    };
  }

  /**
   * Two email switches, split by WHY we would write rather than by how loud
   * the email is:
   *   - activity: something happened in your account and you may want to act
   *   - offers: we want to sell you something
   *
   * Hidden for guests: /account-settings has no route-level auth guard, so a
   * guest can reach it by URL, and the PATCH behind these switches requires a
   * full account.
   *
   * Neither switch governs account mail — password resets, email verification,
   * partner invitations. Turning both off must not read as "I will never hear
   * from you again", so the footnote states that outright rather than leaving
   * the user to infer it from the two hints.
   */
  renderNotificationsSection() {
    if (this.user?.isGuest) return '';

    const prefs = this.notificationPrefs;
    return `
      <section class="account-section">
        <h2 class="account-section__label">Notifications</h2>
        <div class="card account-section__card">
          ${renderSwitchRow({
            label: 'Activity emails',
            hint: 'Partner notes, new guides, and other activity in your account worth knowing about.',
            id: 'account-notif-system',
            on: prefs.systemEmails,
            disabled: this.prefsSaving,
            action: 'toggle-system-emails'
          })}
          ${renderSwitchRow({
            label: 'Offers &amp; promotions',
            hint: 'Discounts, trials, and occasional news about new features.',
            id: 'account-notif-marketing',
            on: prefs.marketingEmails,
            disabled: this.prefsSaving,
            action: 'toggle-marketing-emails'
          })}
          <p class="account-note account-note--footnote">
            We&rsquo;ll always email you about your account &mdash; password resets,
            sign-in changes, and partner invitations.
          </p>
        </div>
      </section>
    `;
  }

  renderPrivacySection() {
    return `
      <section class="account-section">
        <h2 class="account-section__label">Privacy &amp; data</h2>
        <div class="card account-section__card">
          <div class="account-row">
            <div class="account-row__body">
              <span class="account-row__label">Download your data</span>
              <span class="account-row__value">Export a copy of your sessions and insights.</span>
            </div>
            <div class="account-row__action">
              <span class="account-chip">Coming soon</span>
            </div>
          </div>
          <div class="account-row">
            <div class="account-row__body">
              <span class="account-row__label">Delete account</span>
              <span class="account-row__value">Permanently remove your account and data.</span>
            </div>
            <div class="account-row__action">
              <span class="account-chip">Coming soon</span>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  /**
   * Insert profile avatars after the template is mounted. Rendering them
   * imperatively keeps image, initial, and generic-icon fallback behavior in
   * createProfileAvatar.
   */
  populateAvatar() {
    const slot = this.container.querySelector('#account-profile-avatar');
    if (slot && this.user) {
      slot.replaceChildren(createProfileAvatar(this.user, { size: 56 }));
    }

    const partnerSlot = this.container.querySelector('[data-profile-avatar="partner"]');
    if (partnerSlot && this.partner) {
      partnerSlot.replaceChildren(createProfileAvatar(this.partner, { size: 20 }));
    }
  }

  attachListeners() {
    this.container.addEventListener('click', this.handleClick);
    this.container.addEventListener('submit', this.handleSubmit);
    this.container.addEventListener('keydown', this.handleKeydown);
  }

  detachListeners() {
    this.container.removeEventListener('click', this.handleClick);
    this.container.removeEventListener('submit', this.handleSubmit);
    this.container.removeEventListener('keydown', this.handleKeydown);
  }

  handleClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target || !this.container.contains(target)) return;
    const action = target.getAttribute('data-action');
    if (!action) return;
    event.preventDefault();

    switch (action) {
      case 'edit-name':
        this.editingName = true;
        this.rerender({ focus: 'name-input' });
        break;
      case 'cancel-name':
        this.editingName = false;
        this.rerender();
        break;
      case 'change-password':
        this.changingPassword = true;
        this.passwordError = '';
        this.rerender({ focus: 'current-password' });
        break;
      case 'cancel-change-password':
        this.changingPassword = false;
        this.passwordError = '';
        this.rerender();
        break;
      case 'sign-out':
        eventBus.emit('profile:signout');
        break;
      case 'toggle-system-emails':
        this.toggleNotificationPref('systemEmails', action);
        break;
      case 'toggle-marketing-emails':
        this.toggleNotificationPref('marketingEmails', action);
        break;
      case 'manage-subscription':
        // Mirror ProfilePopup.handleManageSubscription: Basic → /upgrade,
        // Premium, lapsed (UNPAID/CANCELED) and past-due (PAST_DUE) →
        // /subscription, which shows lapsed users the reactivation card and
        // past-due users the payment-issue card. While the fetch is still in
        // flight the button isn't rendered, so the plan is always resolved
        // here. Only the upgrade route counts as an upgrade click.
        if (isPaidPlan(this.subscriptionPlan)
            || this.subscriptionPlan === 'lapsed'
            || this.subscriptionPlan === 'past_due') {
          eventBus.emit('navigate', { page: 'subscription' });
        } else {
          goToUpgrade(eventBus, 'accountSettings');
        }
        break;
      case 'manage-partner':
        // /partner handles both linked and unlinked states.
        eventBus.emit('navigate', { page: 'partner' });
        break;
      default:
        console.warn('[AccountSettingsSection] Unknown action:', action);
    }
  }

  /**
   * Read the shared subscription snapshot and re-render the subscription card.
   * Token-guarded against stale responses. A failed read resolves to null,
   * which falls back to Basic so the upgrade CTA stays actionable.
   *
   * Lapsed and past-due detection match ProfilePopup.fetchSubscriptionStatus:
   * a non-paid plan with UNPAID/CANCELED status means Premium history, so the
   * row offers reactivation instead of an upgrade; a PAST_DUE status means a
   * failed payment inside the grace period, which still reads as premium in
   * the payload but takes precedence over the generic paid-plan rendering.
   */
  async fetchSubscriptionStatus() {
    const token = ++this.subscriptionFetchToken;
    const status = await fetchSharedStatus();
    if (token !== this.subscriptionFetchToken || !this.isActive) return;
    const plan = planFromStatus(status);
    if (status?.status === 'PAST_DUE') {
      this.subscriptionPlan = 'past_due';
    } else {
      const isLapsed = !isPaidPlan(plan)
        && (status?.status === 'UNPAID' || status?.status === 'CANCELED');
      this.subscriptionPlan = isLapsed ? 'lapsed' : plan;
    }
    this.rerender();
  }

  /**
   * Fetch partner and re-render the partner card. Token-guarded. On error,
   * fall back to "unlinked" if we never resolved before so the invite CTA
   * remains reachable.
   */
  async fetchPartner() {
    const token = ++this.partnerFetchToken;
    try {
      const result = await this.discoveryApi.getPartner();
      if (token !== this.partnerFetchToken || !this.isActive) return;
      this.partner = result?.data || null;
      this.rerender();
    } catch (err) {
      if (token !== this.partnerFetchToken || !this.isActive) return;
      console.warn('[AccountSettingsSection] Failed to fetch partner:', err?.error || err);
      if (this.partner === undefined) {
        this.partner = null;
        this.rerender();
      }
    }
  }

  /**
   * Flip one email preference and persist it.
   *
   * The switch moves immediately and reverts if the save fails. Note this does
   * NOT use try/catch the way PartnerSection's sharing toggles do: API methods
   * here never reject — they resolve with `{ success: false, error }` on a
   * rejected request and `null` on a network failure — so the failure check is
   * on the return value, matching saveName() above. A catch would never fire
   * and every failed save would silently look successful.
   *
   * @param {'systemEmails'|'marketingEmails'} key
   * @param {string} action - data-action of the clicked switch, for focus restore
   */
  async toggleNotificationPref(key, action) {
    if (this.prefsSaving) return;

    const previous = this.notificationPrefs[key];
    const next = !previous;

    this.prefsSaving = true;
    this.prefsOverride = { ...(this.prefsOverride || {}), [key]: next };
    this.rerender({ focus: `action:${action}` });

    try {
      const result = await API.updateNotificationPreferences({ [key]: next });

      if (!result || result.success === false) {
        // Drop the optimistic value; the render falls back to the saved one.
        this.prefsOverride = null;
        window.notificationManager?.showError(
          result?.error || 'Could not update that setting. Please try again.'
        );
        return;
      }

      // Adopt what the server actually stored, so both switches stay truthful
      // even if the response differs from what we sent.
      const saved = result.notificationPreferences;
      if (saved && window.app?.currentUser) {
        window.app.currentUser.notificationPreferences = saved;
      }
      this.prefsOverride = null;
    } finally {
      // Instance state is always restored; only the DOM update is conditional,
      // so navigating away mid-flight can't leave a stale value behind.
      this.prefsSaving = false;
      if (this.isActive) this.rerender({ focus: `action:${action}` });
    }
  }

  /**
   * eventBus listener — keeps the partner card in sync when PartnerSection
   * unlinks from a different page surface during this session.
   */
  handlePartnerUnlinked() {
    this.partner = null;
    if (this.isActive) this.rerender();
  }

  handleSubmit(event) {
    const form = event.target.closest('form[data-form]');
    if (!form) return;
    event.preventDefault();

    const kind = form.getAttribute('data-form');
    if (kind === 'name') {
      const input = form.querySelector('input[name="firstName"]');
      this.saveName(input?.value ?? '');
    } else if (kind === 'password') {
      const current = form.querySelector('input[name="currentPassword"]')?.value ?? '';
      const next = form.querySelector('input[name="newPassword"]')?.value ?? '';
      const confirm = form.querySelector('input[name="confirmPassword"]')?.value ?? '';
      this.savePassword(current, next, confirm);
    }
  }

  /** Escape cancels whichever editor is open. */
  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    if (this.editingName) {
      this.editingName = false;
      this.rerender();
    } else if (this.changingPassword) {
      this.changingPassword = false;
      this.passwordError = '';
      this.rerender();
    }
  }

  async saveName(rawValue) {
    const trimmed = (rawValue || '').trim();
    const current = this.user?.firstName || '';

    if (trimmed.length === 0) {
      window.notificationManager?.showError('First name cannot be empty.');
      return;
    }
    if (trimmed.length > 80) {
      window.notificationManager?.showError('First name is too long (max 80 characters).');
      return;
    }
    if (trimmed === current) {
      // No-op save → just collapse.
      this.editingName = false;
      this.rerender();
      return;
    }

    this.nameSaving = true;
    this.rerender({ focus: 'name-input' });

    try {
      const result = await API.updateProfile({ firstName: trimmed });
      if (!result || result.success === false) {
        const msg = result?.error || 'Could not update name. Please try again.';
        window.notificationManager?.showError(msg);
        return;
      }

      const updated = result.user?.firstName ?? trimmed;
      if (window.app?.currentUser) {
        window.app.currentUser.firstName = updated;
        if (result.user?.name !== undefined) {
          window.app.currentUser.name = result.user.name;
        }
      }
      // Reflect the name change in the sidebar button immediately.
      window.app?.updateSidebarProfileButton?.();

      this.editingName = false;
      window.notificationManager?.showSuccess('Name updated.');
    } finally {
      this.nameSaving = false;
      this.rerender();
    }
  }

  async savePassword(currentPassword, newPassword, confirmPassword) {
    // Client-side validation — matches server rules but gives faster feedback.
    if (!currentPassword) {
      this.passwordError = 'Enter your current password.';
      this.rerender({ focus: 'current-password' });
      return;
    }
    if (newPassword.length < 8) {
      this.passwordError = 'New password must be at least 8 characters.';
      this.rerender();
      return;
    }
    if (newPassword !== confirmPassword) {
      this.passwordError = 'New passwords do not match.';
      this.rerender();
      return;
    }
    if (newPassword === currentPassword) {
      this.passwordError = 'New password must differ from your current password.';
      this.rerender();
      return;
    }

    this.passwordSaving = true;
    this.passwordError = '';
    this.rerender();

    try {
      const result = await API.passwordChange(currentPassword, newPassword);
      if (!result || result.success === false) {
        this.passwordError = result?.error || 'Could not change password. Please try again.';
        return;
      }

      this.changingPassword = false;
      this.passwordError = '';
      window.notificationManager?.showSuccess('Password updated.');
    } finally {
      this.passwordSaving = false;
      this.rerender();
    }
  }
}

export default AccountSettingsSection;
